import { useContext, useEffect, useRef, useState, type MouseEvent, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Typography,
  Spin,
  Form,
  Select,
  Button,
  DatePicker,
  Input,
  Modal,
} from "antd";
import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { journalsQueries, JournalRecordPageShell } from "@/entities/journal";
import { AbilityContext } from "@/shared/lib/casl";
import { useLocalUser, useCoordinator } from "@/shared/lib/hooks";
import { useUnifiedAutoResize, getLatestIsoDate } from "@/shared/utils";
import { useAppRoutes } from "@shared/lib/react-router";
import {
  JournalCryptographyWrapper,
  JournalRecordInfo,
  JournalSigners,
} from "@shared/ui";
import {
  SectionFourActionEnum,
  SectionFourActionTypes,
} from "../../model";
import { getSectionFourActions } from "../../model/actions-map";
import { useSetupSigningString } from "../../model/hooks/setup-signing-string";
import { useSectionFourController } from "../../model/hooks/use-section-four-controller";
import { useSectionFourSecondPartController } from "../../model/hooks/use-section-four-second-part-controller";
import { useWithSignedString } from "../../model/hooks/with-signed-string";
import {
  buildSectionFourSignersList,
  formatSectionFourSignerLabel,
} from "../../model/utils/format-section-four-signer-label";
import { SectionDocumentFourList } from "../journal-section-form";
import { useModalInfo } from "../journal-section-form/model";
import { useCreateSectionTwoFourEntry } from "../journal-section-form/ui/journal-section-four-info/model/hooks/use-create-section-four-two-entry";
import { useUpdateSectionFourTwoEntry } from "../journal-section-form/ui/journal-section-four-info/model/hooks/use-update-section-four-two-entry";
import { useUpdateSectionFourTwoEntrySigned } from "../journal-section-form/ui/journal-section-four-info/model/hooks/use-update-section-four-two-entry-signed";
import {
  SectionFourthControlProcedure,
  SectionFourthControlSubject,
  SectionFourthDocuments,
  SectionFourthViolationDocuments,
  SectionFourthActions,
} from "../journal-section-form/ui/journal-section-four-info/ui";
import { useGetItemsVersion } from "../journals-section-fourth/model";
import "./journal-section-four-info-page.scss";

const { Option } = Select;
const { Title } = Typography;

export const JournalSectionFourInfoPage = () => {
  const navigate = useNavigate();
  const { itemNoVersionId } = useParams<{ itemNoVersionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const localUser = useLocalUser();
  const { mainJournalId, titleId, projectId, objectId } = useCoordinator();
  const appRoutes = useAppRoutes();

  const ability = useContext(AbilityContext);
  const hasEditPermission = ability?.can("edit", "section_4");

  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const {
    handleSendToSign,
    handleReturn,
    handleStatusReturn,
    handleDelete,
    handleReject,
  } = useSectionFourController(
    mainJournalData?.mainJournal?.section4?.sectionListId || "",
    mainJournalData?.mainJournal?.section4?.sectionId || "",
  );

  const {
    handleSendToSign: handleSendToSignTwo,
    handleReturn: handleReturnTwo,
    handleStatusReturn: handleStatusReturnTwo,
    handleReject: handleRejectTwo,
  } = useSectionFourSecondPartController(
    mainJournalData?.mainJournal?.section4?.sectionListId || "",
    mainJournalData?.mainJournal?.section4?.sectionId || "",
  );

  const { data: titleJournal } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const signersList = buildSectionFourSignersList(
    titleJournal?.mainJournal?.titleJournal?.developer?.constructionControls,
    titleJournal?.mainJournal?.titleJournal?.technicalCustomer
      ?.constructionControls,
  );
  const { data: itemInfo, isFetching } = useModalInfo(itemNoVersionId || "");
  const { data: versions } = useGetItemsVersion(
    mainJournalData?.mainJournal?.section4?.sectionListId,
    itemNoVersionId || undefined,
  );
  const {
    signingString,
    setupSigningStringPartOne,
    setupSigningStringPartTwo,
  } = useSetupSigningString(mainJournalData);
  const { withSignedStringPartOne, withSignedStringPartTwo } =
    useWithSignedString();
  const {
    setValue,
    formState: { errors },
  } = useForm<any>();

  useEffect(() => {
    if (itemInfo?.itemPartOne?.docs) {
      const docs = itemInfo.itemPartOne.docs.map((doc: any) => ({
        id: doc.id,
        docId: doc.docId,
        docType: doc.docType,
        docTitle: doc.name,
        documentUrl: doc.documentUrl,
        docInfo: doc.docInfo,
      }));
      setValue("docs", docs);
    }
    if (itemInfo?.itemPartOne?.otherDocuments) {
      const otherDocs = itemInfo.itemPartOne.otherDocuments.map(
        (doc: any, index: number) => ({
          fileMetadata: {
            fileNumber: index,
            fileName: doc.fileName,
            fileType: "application/pdf",
            fileContentLength: 0,
            id: doc.id,
            fileId: doc.file_id,
          },
        }),
      );
      setValue("itemPartOne.otherDocuments", otherDocs);
    }
  }, [itemInfo, setValue]);

  const {
    setValue: setValueTwo,
    control: controlTwo,
    getValues: getValuesTwo,
    formState: { errors: errorsTwo },
    handleSubmit,
  } = useForm<any>();

  const { mutate: addRecord, isPending } = useCreateSectionTwoFourEntry({
    itemNoVersionId: itemNoVersionId || "",
    section4ListId: mainJournalData?.mainJournal?.section4?.sectionListId || "",
  });
  const { mutate: updateRecord, isPending: isUpdating } =
    useUpdateSectionFourTwoEntry({
      itemNoVersionId: itemNoVersionId || "",
      section4ListId:
        mainJournalData?.mainJournal?.section4?.sectionListId || "",
    });
  const { mutate: updateRecordSigned, isPending: isUpdatingSigned } =
    useUpdateSectionFourTwoEntrySigned({
      section4ListId:
        mainJournalData?.mainJournal?.section4?.sectionListId || "",
      versionId: itemInfo?.currentVersionId || "",
      itemPartTwoId: itemInfo?.itemPartTwo?.id || "",
    });

  const isPartTwoSelected =
    Boolean(searchParams.get("itemPartTwo")) ||
    itemInfo?.itemPartTwo?.status === "AWAITING_SIGNATURE";
  const statusPartOne = itemInfo?.itemPartOne?.status || "";
  const statusPartTwo = itemInfo?.itemPartTwo?.status || "";
  const effectiveStatus = isPartTwoSelected ? statusPartTwo : statusPartOne;
  const actions = getSectionFourActions(effectiveStatus, Boolean(hasEditPermission));
  const actionsPartOne = getSectionFourActions(statusPartOne, Boolean(hasEditPermission));
  const actionsPartTwo = getSectionFourActions(statusPartTwo, Boolean(hasEditPermission));
  const hasVersions = Boolean((itemInfo as any)?.currentVersionId);
  const canSendToSign =
    actionsPartOne.includes("sendToSign") ||
    actionsPartTwo.includes("sendToSign");

  const sectionTwoActionParam = searchParams.get("sectionTwoAction");
  const sectionTwoAction = sectionTwoActionParam
    ? (sectionTwoActionParam as SectionFourActionEnum)
    : SectionFourActionTypes.VIEW;
  const isSecondPartFormMode =
    sectionTwoAction === SectionFourActionTypes.CREATE ||
    sectionTwoAction === SectionFourActionTypes.EDIT;
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectCommentError, setRejectCommentError] = useState("");
  const [rejectPart, setRejectPart] = useState<"partOne" | "partTwo">(
    "partOne",
  );
  const [rejectPartId, setRejectPartId] = useState("");

  useUnifiedAutoResize();

  const handleClose = () => {
    navigate(
      appRoutes.journals.section4(projectId, objectId, mainJournalId, titleId),
    );
  };

  const handleIsForm = (
    status: boolean,
    event?: MouseEvent<HTMLSpanElement>,
  ) => {
    if (event) {
      event.stopPropagation();
    }
    if (!status) {
      navigate(
        appRoutes.journals.section4(projectId, objectId, mainJournalId, titleId),
      );
    }
  };

  const handleCloseInfo = () => {
    navigate(
      appRoutes.journals.section4(projectId, objectId, mainJournalId, titleId),
    );
  };

  const handleOpenReject = (
    part: "partOne" | "partTwo",
    partId: string,
  ) => {
    if (!partId) return;

    const params = new URLSearchParams(searchParams);
    params.set("itemNoVersionId", itemNoVersionId || "");
    if (part === "partTwo") {
      params.set("itemPartTwo", partId);
      params.delete("itemPartOne");
    } else {
      params.set("itemPartOne", partId);
      params.delete("itemPartTwo");
    }
    setSearchParams(params);

    setRejectPart(part);
    setRejectPartId(partId);
    setRejectComment("");
    setRejectCommentError("");
    setRejectModalOpen(true);
  };

  const handleCloseRejectModal = () => {
    setRejectModalOpen(false);
    setRejectComment("");
    setRejectCommentError("");
    setRejectPartId("");
  };

  const handleSubmitReject = () => {
    if (!rejectComment.trim()) {
      setRejectCommentError("Укажите причину отклонения");
      return;
    }

    if (!itemNoVersionId || !rejectPartId) return;

    if (rejectPart === "partTwo") {
      handleRejectTwo({
        itemNoVersionId,
        itemPartTwoId: rejectPartId,
        comment: rejectComment,
        signerEmployeeId: localUser?.user_id || "",
      });
    } else {
      handleReject({
        itemNoVersionId,
        itemPartOneId: rejectPartId,
        comment: rejectComment,
        signerEmployeeId: localUser?.user_id || "",
      });
    }

    handleCloseRejectModal();
  };

  const onSubmit = () => {
    const repId = getValuesTwo("controlRepresentative");
    const defectFixingFactDate = getValuesTwo("defectFixingFactDate");

    const files = getValuesTwo("itemPartTwo.files") || [];
    const rawDocs = getValuesTwo("itemPartTwo.otherDocuments") || [];

    const preparedDocs = rawDocs.map((doc: any, index: number) => ({
      ...doc,
      file: files[index],
    }));

    const originalDocs = itemInfo?.itemPartTwo?.otherDocuments || [];

    const createDocs = preparedDocs.filter(
      (doc: any) => !doc.fileMetadata?.id && !doc.fileMetadata?.fileId,
    );

    const deleteDocs = originalDocs
      .filter((original: any) => {
        return !preparedDocs.some(
          (doc: any) =>
            doc.fileMetadata?.fileId === original.file_id &&
            doc.fileMetadata?.fileName === original.fileName,
        );
      })
      .filter((removed: any) => removed.id && removed.file_id)
      .map((removed: any) => ({
        file: {
          id: removed.id as string,
          fileId: removed.file_id as string,
        },
      }));

    if (sectionTwoAction === SectionFourActionTypes.EDIT) {
      const isSigned = itemInfo?.itemPartTwo?.status === "SIGNED";
      if (isSigned) {
        updateRecordSigned(
          {
            mainJournalId: mainJournalData?.mainJournal?.mainJournalId || "",
            section4Id: mainJournalData?.mainJournal?.section4?.sectionId || "",
            section4ListId:
              mainJournalData?.mainJournal?.section4?.sectionListId || "",
            itemNoVersionId: itemNoVersionId || "",
            itemPartOne: itemInfo?.itemPartOne?.id || "",
            itemPartTwo: {
              defectFixingFactDate,
              createOtherDocs: createDocs,
              deleteOtherDocs: deleteDocs,
              defectFixingInspectionEmployeeId: repId,
            },
            actionDetails: getValuesTwo("actionDetails") || "",
          },
          {
            onSuccess: () => {
              navigate(
              appRoutes.journals.section4(projectId, objectId, mainJournalId, titleId),
            );
            },
          },
        );
      } else {
        updateRecord(
          {
            mainJournalId: mainJournalData?.mainJournal?.mainJournalId || "",
            section4Id: mainJournalData?.mainJournal?.section4?.sectionId || "",
            section4ListId:
              mainJournalData?.mainJournal?.section4?.sectionListId || "",
            itemPartOneId: itemInfo?.itemPartOne?.id || "",
            itemPartTwo: {
              defectFixingFactDate,
              createOtherDocs: createDocs,
              deleteOtherDocs: deleteDocs,
              defectFixingInspectionEmployeeId: repId,
            },
          },
          {
            onSuccess: () => {
              navigate(
              appRoutes.journals.section4(projectId, objectId, mainJournalId, titleId),
            );
            },
          },
        );
      }
    } else {
      addRecord(
        {
          mainJournalId: mainJournalData?.mainJournal?.mainJournalId || "",
          section4Id: mainJournalData?.mainJournal?.section4?.sectionId || "",
          section4ListId:
            mainJournalData?.mainJournal?.section4?.sectionListId || "",
          itemPartOneId: itemInfo?.itemPartOne?.id || "",
          itemPartTwo: {
            defectFixingFactDate,
            otherDocuments: preparedDocs,
            defectFixingInspectionEmployeeId: repId,
          },
        },
        {
          onSuccess: () => {
            navigate(
              appRoutes.journals.section4(projectId, objectId, mainJournalId, titleId),
            );
          },
        },
      );
    }
  };

  useEffect(() => {
    if (
      (sectionTwoAction === SectionFourActionTypes.EDIT ||
        sectionTwoAction === SectionFourActionTypes.VIEW) &&
      itemInfo?.itemPartTwo
    ) {
      setValueTwo(
        "defectFixingFactDate",
        itemInfo.itemPartTwo.defectFixingFactDate,
      );

      setValueTwo(
        "controlRepresentative",
        itemInfo.itemPartTwo.defectFixingInspectionEmployee
          ?.defectFixingInspectionEmployeeId,
      );

      const updateOtherDocs =
        itemInfo.itemPartTwo.otherDocuments?.map((doc: any, index: number) => ({
          fileMetadata: {
            fileNumber: index,
            fileName: doc.fileName,
            fileType: "application/pdf",
            fileContentLength: 0,
            id: doc.id,
            fileId: doc.file_id,
          },
        })) || [];

      setValueTwo("itemPartTwo.otherDocuments", updateOtherDocs);
    }
  }, [sectionTwoAction, itemInfo, setValueTwo]);

  // Автоматическое обновление высоты iframe в unified режиме
  const wrapperRef = useRef<HTMLDivElement>(null);
  useUnifiedAutoResize(wrapperRef as RefObject<HTMLElement>);

  const currentVersion = versions?.section4List?.reduce(
    (latest: any, current: any) => {
      if (!latest) return current;
      return (current.versionInfo?.versionNumber || 0) >
        (latest.versionInfo?.versionNumber || 0)
        ? current
        : latest;
    },
    null,
  );
  const currentVersionNumber =
    currentVersion?.versionInfo?.versionNumber || 1;
  const currentVersionSigner =
    currentVersion?.content?.itemPartTwo?.defectFixingInspectionEmployee ||
    currentVersion?.content?.itemPartOne?.inspectionEmployee;
  const currentVersionDate =
    currentVersion?.content?.itemPartTwo?.updateDate ||
    currentVersion?.content?.itemPartOne?.updateDate ||
    currentVersion?.versionInfo?.updateDate;
  const itemUpdateDate = getLatestIsoDate(
    itemInfo?.updateDate,
    itemInfo?.itemPartOne?.updateDate,
    itemInfo?.itemPartTwo?.updateDate,
  );

  return (
    <JournalRecordPageShell ref={wrapperRef} onClose={handleClose}>
        <Spin spinning={isFetching}>
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="journal-info-header journal-info-header--info-page">
              <Title level={3}>
                {isSecondPartFormMode
                  ? "Внесение сведений об исправлении недостатков"
                  : `Раздел № 4. Просмотр записи номер № ${itemInfo?.sequenceNumber}`}
              </Title>

                <JournalRecordInfo
                  updateDate={itemUpdateDate}
                  updateUserId={itemInfo?.updateUserId}
                  status={itemInfo?.itemPartOne?.status}
                  statusUpdateDate={currentVersionDate || itemUpdateDate}
                  currentVersionId={itemInfo?.currentVersionId}
                  currentVersionNumber={currentVersionNumber}
                  signerInfo={
                    currentVersionSigner ||
                    itemInfo?.itemPartOne?.inspectionEmployee
                  }
                />
                <SectionFourthControlProcedure
                  constructionControlInfo={
                    itemInfo?.itemPartOne?.constructionControlInfo
                  }
                  controlEventPeriod={itemInfo?.itemPartOne?.controlEventPeriod}
                  defectDescriptionText={
                    itemInfo?.itemPartOne?.defectDescriptionText
                  }
                  defectFixingPlanDate={itemInfo?.itemPartOne?.defectFixingPlanDate}
                  locationDescription={
                    itemInfo?.itemPartOne?.locationDescription
                  }
                  inspectionEmployee={itemInfo?.itemPartOne?.inspectionEmployee}
                />

                {itemInfo?.itemPartOne?.isDefects ? (
                  <>
                    <SectionFourthControlSubject
                      structuralElementName={
                        itemInfo?.itemPartOne?.structuralElementName
                      }
                      locationDescription={
                        itemInfo?.itemPartOne?.locationDescription
                      }
                      works={itemInfo?.itemPartOne?.works}
                      projectId={projectId}
                      objectId={objectId}
                      mainJournalId={mainJournalId}
                      titleId={titleId}
                    />
                    <SectionFourthDocuments
                      docs={itemInfo?.itemPartOne?.docs}
                      otherDocuments={itemInfo?.itemPartOne?.otherDocuments}
                    />
                    <SectionFourthViolationDocuments
                      violationDocuments={itemInfo?.itemPartTwo?.otherDocuments}
                    />
                  </>
                ) : null}

                <JournalSigners
                  title="Подписанты"
                  signers={[
                    {
                      id: "part-one-signer",
                      title:
                        "Уполномоченный представитель по вопросам строительного контроля",
                      signerLabel:
                        "Уполномоченный представитель по вопросам строительного контроля",
                      currentItem: itemInfo?.itemPartOne,
                      signerFio: `${itemInfo?.itemPartOne?.inspectionEmployee?.surname || ""} ${
                        itemInfo?.itemPartOne?.inspectionEmployee
                          ?.firstName?.[0] || ""
                      }.${itemInfo?.itemPartOne?.inspectionEmployee?.middleName?.[0] || ""}`,
                      isSignerAwaiting:
                        itemInfo?.itemPartOne?.status ===
                          "AWAITING_SIGNATURE" &&
                        itemInfo?.itemPartOne?.inspectionEmployee
                          ?.inspectionEmployeeId === localUser?.user_id,
                      sectionListId:
                        mainJournalData?.mainJournal?.section4?.sectionListId,
                      itemNoVersionId: itemNoVersionId || "",
                      localUser,
                      signingString,
                      isSigningPending: false,
                      getSigningXml: async (params: any) => {
                        await setupSigningStringPartOne(
                          {
                            sectionFourListId: params.sectionFourListId,
                            itemNoVersionId: params.itemNoVersionId,
                            itemPartOneId: itemInfo?.itemPartOne?.id || "",
                            signerEmployeeId: params.signerEmployeeId,
                          },
                          () => {},
                        );
                      },
                      setSigningString: () => {},
                      withSignedString: (params: any, xml: string, mchdId?: string) =>
                        withSignedStringPartOne(
                          {
                            ...params,
                            itemPartOneId: itemInfo?.itemPartOne?.id || "",
                          },
                          xml,
                          mchdId,
                        ),
                      invalidateItem: () => {},
                      openReject: () => {
                        handleOpenReject(
                          "partOne",
                          itemInfo?.itemPartOne?.id || "",
                        );
                      },
                      canAction: "section_4",
                    },
                    ...(itemInfo?.itemPartTwo
                      ? [
                          {
                            id: "part-two-signer",
                            title:
                              "Уполномоченный представитель по вопросам строительного контроля",
                            signerLabel:
                              "Уполномоченный представитель по вопросам строительного контроля",
                            currentItem: itemInfo?.itemPartTwo,
                            signerFio: `${itemInfo?.itemPartTwo?.defectFixingInspectionEmployee?.surname || ""} ${
                              itemInfo?.itemPartTwo
                                ?.defectFixingInspectionEmployee
                                ?.firstName?.[0] || ""
                            }.${itemInfo?.itemPartTwo?.defectFixingInspectionEmployee?.middleName?.[0] || ""}`,
                            isSignerAwaiting:
                              itemInfo?.itemPartTwo?.status ===
                                "AWAITING_SIGNATURE" &&
                              itemInfo?.itemPartTwo
                                ?.defectFixingInspectionEmployee
                                ?.defectFixingInspectionEmployeeId ===
                                localUser?.user_id,
                            sectionListId:
                              mainJournalData?.mainJournal?.section4
                                ?.sectionListId,
                            itemNoVersionId: itemNoVersionId || "",
                            localUser,
                            signingString,
                            isSigningPending: false,
                            getSigningXml: async (params: any) => {
                              await setupSigningStringPartTwo(
                                {
                                  sectionFourListId: params.sectionFourListId,
                                  itemNoVersionId: params.itemNoVersionId,
                                  itemPartTwoId:
                                    itemInfo?.itemPartTwo?.id || "",
                                  signerEmployeeId: params.signerEmployeeId,
                                },
                                () => {},
                              );
                            },
                            setSigningString: () => {},
                            withSignedString: (params: any, xml: string, mchdId?: string) =>
                              withSignedStringPartTwo(
                                {
                                  ...params,
                                  itemPartTwoId: itemInfo?.itemPartTwo?.id || "",
                                },
                                xml,
                                mchdId,
                              ),
                            invalidateItem: () => {},
                            openReject: () => {
                              handleOpenReject(
                                "partTwo",
                                itemInfo?.itemPartTwo?.id || "",
                              );
                            },
                            canAction: "section_4",
                          },
                        ]
                      : []),
                  ]}
                />

                {sectionTwoAction === SectionFourActionTypes.CREATE ||
                sectionTwoAction === SectionFourActionTypes.EDIT ? (
                  <>
                    <SectionDocumentFourList
                      uploadConfigMultiFour={[]}
                      title="Документы подтверждающие исправление недостатков "
                      setValue={setValueTwo}
                      errors={errorsTwo}
                      getValues={getValuesTwo}
                      control={controlTwo}
                      isOtherDocs
                      fieldName="itemPartTwo"
                    />
                    <Form.Item
                      label="Фактическая дата устранения"
                      style={{ marginBottom: 24 }}
                      validateStatus={
                        errorsTwo.defectFixingFactDate ? "error" : ""
                      }
                      help={errorsTwo.defectFixingFactDate?.message?.toString()}
                    >
                      <Controller
                        name="defectFixingFactDate"
                        control={controlTwo}
                        rules={{ required: "Укажите дату устранения" }}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            format="DD.MM.YYYY"
                            style={{ width: "100%" }}
                            placeholder="Выберите дату"
                            value={field.value ? dayjs(field.value) : null}
                            onChange={(date) =>
                              field.onChange(date ? date.toISOString() : null)
                            }
                          />
                        )}
                      />
                    </Form.Item>
                    <Form.Item
                      label="Уполномоченный представитель по вопросам строительного контроля"
                      validateStatus={
                        errors.controlRepresentative ? "error" : ""
                      }
                      help={errors.controlRepresentative?.message?.toString()}
                      style={{ marginBottom: 24 }}
                    >
                      <Controller
                        name="controlRepresentative"
                        control={controlTwo}
                        rules={{ required: "Выберите представителя" }}
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder="Выберите представителя"
                            optionLabelProp="label"
                          >
                            {signersList.map((signer) => {
                              const label = formatSectionFourSignerLabel(signer);

                              return (
                                <Option
                                  key={signer.user?.userId}
                                  value={signer?.user?.userId}
                                  label={label}
                                >
                                  {label}
                                </Option>
                              );
                            })}
                          </Select>
                        )}
                      />
                      <div className="journal-form-hint">
                        Этому человеку будет отправлена данная запись на
                        подписание
                      </div>
                      {itemInfo?.itemPartTwo?.status === "AWAITING_SIGNATURE" &&
                        itemInfo?.itemPartTwo?.defectFixingInspectionEmployee
                          ?.defectFixingInspectionEmployeeId ===
                          localUser?.user_id && (
                          <div className="journal-sign-actions-row">
                            <JournalCryptographyWrapper
                              currentUser={localUser as any}
                              signContent={signingString}
                              onSuccess={(xml, _proxyId, mchdId) =>
                                withSignedStringPartTwo(
                                  {
                                    sectionFourListId:
                                      mainJournalData?.mainJournal?.section4
                                        ?.sectionListId || "",
                                    itemNoVersionId: itemNoVersionId || "",
                                    itemPartTwoId:
                                      itemInfo?.itemPartTwo?.id || "",
                                    signerEmployeeId: localUser?.user_id || "",
                                  },
                                  xml,
                                  mchdId,
                                )
                              }
                            >
                              {({ onOpen }: any) => (
                                <Button
                                  type="primary"
                                  onClick={() =>
                                    setupSigningStringPartTwo(
                                      {
                                        sectionFourListId:
                                          mainJournalData?.mainJournal?.section4
                                            ?.sectionListId || "",
                                        itemNoVersionId: itemNoVersionId || "",
                                        itemPartTwoId:
                                          itemInfo?.itemPartTwo?.id || "",
                                        signerEmployeeId:
                                          localUser?.user_id || "",
                                      },
                                      onOpen,
                                    )
                                  }
                                >
                                  Подписать
                                </Button>
                              )}
                            </JournalCryptographyWrapper>
                            <Button
                              type="dashed"
                              onClick={() => {
                                handleOpenReject(
                                  "partTwo",
                                  itemInfo?.itemPartTwo?.id || "",
                                );
                              }}
                            >
                              Отклонить
                            </Button>
                          </div>
                        )}
                    </Form.Item>
                    {itemInfo?.itemPartTwo?.status === "SIGNED" && (
                      <Form.Item
                        label="Причина изменения записи"
                        style={{ marginBottom: 24 }}
                      >
                        <Controller
                          name="actionDetails"
                          control={controlTwo}
                          rules={{
                            required: "Укажите причину изменения записи",
                          }}
                          render={({ field }) => (
                            <Input.TextArea
                              {...field}
                              placeholder="Опишите причину изменения подписанной записи"
                              rows={2}
                              maxLength={255}
                              showCount
                            />
                          )}
                        />
                      </Form.Item>
                    )}

                    <Button
                      loading={isPending || isUpdating || isUpdatingSigned}
                      type="primary"
                      htmlType="submit"
                      style={{ marginRight: 8, height: 44, marginBottom: 16 }}
                    >
                      Обновить запись
                    </Button>
                  </>
                ) : null}

                <SectionFourthActions
                  canSendToSign={canSendToSign}
                  canViewVersions={hasVersions}
                  itemNoVersionId={itemNoVersionId}
                  itemInfo={itemInfo}
                  actions={actions}
                  actionsPartTwo={actionsPartTwo}
                  isPartTwoSelected={isPartTwoSelected}
                  handleIsForm={handleIsForm}
                  handleCloseInfo={handleCloseInfo}
                  sectionTwoAction={sectionTwoAction}
                  handleSendToSign={handleSendToSign}
                  handleSendToSignTwo={handleSendToSignTwo}
                  handleReturn={handleReturn}
                  handleReturnTwo={handleReturnTwo}
                  handleStatusReturn={handleStatusReturn}
                  handleStatusReturnTwo={handleStatusReturnTwo}
                  handleDelete={handleDelete}
                />
            </div>
          </Form>
        </Spin>
      <Modal
        centered
        title="Отклонить подписание"
        open={rejectModalOpen}
        onCancel={handleCloseRejectModal}
        onOk={handleSubmitReject}
        okText="Отклонить"
        cancelText="Отмена"
        destroyOnClose
      >
        <div className="journal-reject-modal-body">
          Вы действительно хотите отклонить подписание?
        </div>
        <Form layout="vertical">
          <Form.Item
            label="Комментарий"
            validateStatus={rejectCommentError ? "error" : ""}
            help={rejectCommentError || undefined}
            style={{ marginBottom: 0 }}
          >
            <Input.TextArea
              rows={3}
              value={rejectComment}
              onChange={(e) => {
                setRejectComment(e.target.value);
                if (rejectCommentError) setRejectCommentError("");
              }}
              maxLength={255}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </JournalRecordPageShell>
  );
};

